import React from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Phone, Mail, MapPin, Clock, ShieldCheck, Star } from 'lucide-react'
import ContactForm from '../components/ContactForm'
import { updateMeta } from '../lib/utils'

export default function ContactPage() {
  React.useEffect(() => {
    updateMeta(
      "Kontakt os | Thellefsen Regnskab",
      "Kontakt Thellefsen Regnskab for et uforpligtende tilbud. Vi svarer inden for 24 timer. Ring eller send en besked via vores kontaktformular."
    )
  }, [])

  const [searchParams] = useSearchParams()
  const type = searchParams.get('type')
  const plan = searchParams.get('plan')

  // Customize form title/subtitle based on query parameters
  let formTitle = 'Kontakt os i dag'
  let formSubtitle = ''
  let defaultService = ''

  if (type === 'samtale') {
    formTitle = 'Book en gratis samtale'
    formSubtitle = 'Vælg et tidspunkt, der passer dig. Samtalen tager ca. 15 minutter og er helt uforpligtende.'
    defaultService = 'andet'
  } else if (type === 'pris') {
    formTitle = `Få fast pris på ${plan || 'din opgave'}`
    formSubtitle = 'Udfyld formularen, så vender vi tilbage inden for 24 timer.'
    if (plan && plan.toLowerCase().includes('regnskab')) {
      defaultService = 'aarsregnskab'
    } else if (plan && plan.toLowerCase().includes('bogføring')) {
      defaultService = 'aarsregnskab-og-bogfoering'
    }
  }

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      {/* Header Section */}
      <section className="bg-white border-b border-stone-200 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 tracking-tight">
            Kontakt Os
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Har du spørgsmål til regnskab eller ønsker et uforpligtende tilbud? Hos{' '}
            <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
              Thellefsen Regnskab
            </Link>{' '}
            sidder vi klar til at hjælpe dig trygt videre.
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Contact Info Column */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                  Kontaktoplysninger
                </h2>
              </div>

              {/* Contact Details Cards */}
              <div className="divide-y divide-stone-200">
                {/* Phone */}
                <div className="flex items-start gap-4 py-5 first:pt-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center text-stone-900">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <a href="tel:+4531335470" className="text-lg font-semibold text-orange-600 hover:underline transition-colors mt-0.5 block">
                      31 33 54 70
                    </a>
                    <p className="text-xs text-stone-500 mt-1">Hverdage kl. 08:30 - 16:30 (Fredag til 15:30)</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4 py-5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center text-stone-900">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <a href="mailto:info@tregnskab.dk" className="text-lg font-semibold text-orange-600 hover:underline transition-colors mt-0.5 block">
                      info@tregnskab.dk
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4 py-5 last:pb-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center text-stone-900">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-stone-700 text-sm mt-1 leading-relaxed">
                      3060 Espergærde
                    </p>
                    <p className="text-xs text-stone-500 mt-1">Møder kun efter forudgående aftale.</p>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="rounded-none border border-stone-200 bg-white p-6 space-y-3">
                <div className="flex items-center gap-1 text-[#18724a]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <h4 className="font-bold text-stone-900 text-sm">Din tryghed er vores prioritet</h4>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <ContactForm 
                defaultService={defaultService}
                title={formTitle}
                subtitle={formSubtitle}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
